import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { ApplicationRef, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router, Scroll } from '@angular/router';
import { concat, EMPTY, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

const SCROLL_RETRY_INTERVAL = 100;
const SCROLL_RETRY_TIMEOUT = 10_000;

interface AnchorScrollState {
  element: HTMLElement | null;
  maxScrollPosition: number;
  targetScrollPosition: number | null;
}

@Injectable()
export class ScrollService {
  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller,
    private appRef: ApplicationRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Scroll to each navigation fragment once Angular has finished rendering it, `yOffset` below
   * the viewport top (for a sticky nav). Keep it aligned while the dynamic page layout settles;
   * only geometry changes trigger a retry, and a later navigation cancels it.
   */
  scrollToAnchor$(options?: { yOffset?: number; xOffset?: number }): Observable<boolean> {
    if (!isPlatformBrowser(this.platformId)) return EMPTY;

    return this.router.events.pipe(
      filter((event): event is Scroll => event instanceof Scroll),
      switchMap((scrollEvent) => {
        const anchor = scrollEvent.anchor;
        if (!anchor) return EMPTY;

        return this.appRef.isStable.pipe(
          filter(Boolean),
          take(1),
          switchMap(() => this.scrollToAnchorWhileLayoutSettles$(anchor, options)),
        );
      }),
    );
  }

  private scrollToAnchorWhileLayoutSettles$(
    anchor: string,
    options?: { yOffset?: number; xOffset?: number },
  ): Observable<boolean> {
    const yOffset = options?.yOffset ?? 0;

    return concat(of(0), timer(SCROLL_RETRY_INTERVAL, SCROLL_RETRY_INTERVAL)).pipe(
      takeUntil(timer(SCROLL_RETRY_TIMEOUT)),
      map(() => this.getAnchorScrollState(anchor, yOffset)),
      distinctUntilChanged(
        (previous, current) =>
          previous.element === current.element &&
          previous.maxScrollPosition === current.maxScrollPosition &&
          previous.targetScrollPosition === current.targetScrollPosition,
      ),
      filter(
        (state): state is AnchorScrollState & { element: HTMLElement } => state.element !== null,
      ),
      tap(() => {
        this.viewportScroller.setOffset([options?.xOffset ?? 0, yOffset]);
        this.viewportScroller.scrollToAnchor(anchor);
      }),
      map(() => true),
    );
  }

  private getAnchorScrollState(anchor: string, yOffset: number): AnchorScrollState {
    const element = this.document.getElementById(anchor);
    const view = this.document.defaultView;
    const maxScrollPosition = Math.max(
      0,
      Math.max(this.document.documentElement.scrollHeight, this.document.body?.scrollHeight ?? 0) -
        (view?.innerHeight ?? 0),
    );

    if (!element || !view) {
      return { element: null, maxScrollPosition, targetScrollPosition: null };
    }

    return {
      element,
      maxScrollPosition,
      targetScrollPosition: Math.max(
        0,
        element.getBoundingClientRect().top + view.scrollY - yOffset,
      ),
    };
  }
}
